import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiKeyApi } from '@/lib/api';
import type { ApiKey, ApiKeyPermissionGroup } from '@/types';
import { Button, buttonVariants } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn } from '@/lib/utils';

export function ApiKeyManager() {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [permissionGroups, setPermissionGroups] = useState<ApiKeyPermissionGroup[]>([]);
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [newName, setNewName] = useState('');
  const [loading, setLoading] = useState(false);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [createdKey, setCreatedKey] = useState<{ name: string; value: string } | null>(null);

  const fetchKeys = async () => {
    setError(null);
    try {
      setApiKeys(await apiKeyApi.list());
    } catch {
      setError('Failed to load API keys');
    }
  };

  const fetchPermissionCatalog = async () => {
    setCatalogError(null);
    try {
      const groups = await apiKeyApi.listPermissions();
      setPermissionGroups(groups);
      if (selectedPermissions.length === 0) {
        setSelectedPermissions(groups.flatMap(group => group.permissions.map(p => p.code)));
      }
    } catch {
      setCatalogError('Failed to load API key permissions');
    } finally {
      setCatalogLoading(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        await Promise.all([fetchKeys(), fetchPermissionCatalog()]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const togglePermission = (code: string, checked: boolean) => {
    setSelectedPermissions(prev =>
      checked ? [...new Set([...prev, code])] : prev.filter(p => p !== code)
    );
  };

  const handleCreate = async () => {
    if (!newName.trim() || selectedPermissions.length === 0) return;
    setCreating(true);
    setError(null);
    try {
      const created = await apiKeyApi.create(newName.trim(), selectedPermissions);
      setCreatedKey({
        name: created.name,
        value: created.apiKey ?? created.apikey ?? '',
      });
      setNewName('');
      await fetchKeys();
    } catch {
      setError('Failed to create API key');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    setError(null);
    try {
      await apiKeyApi.remove(id);
      if (editingId === id) {
        setEditingId(null);
      }
      await fetchKeys();
    } catch {
      setError('Failed to delete API key');
    } finally {
      setDeletingId(null);
    }
  };

  const startEdit = (key: ApiKey) => {
    setEditingId(key.id);
    setSelectedPermissions(key.permissions ?? []);
    setError(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setSelectedPermissions(permissionGroups.flatMap(group => group.permissions.map(p => p.code)));
  };

  const handleSaveEdit = async (id: string) => {
    if (selectedPermissions.length === 0) {
      setError('Select at least one permission');
      return;
    }
    setSavingEdit(true);
    setError(null);
    try {
      await apiKeyApi.update(id, { permissions: selectedPermissions });
      setEditingId(null);
      await fetchKeys();
    } catch {
      setError('Failed to update API key permissions');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDismissCreatedKey = () => {
    setCreatedKey(null);
  };

  const handleCopyCreatedKey = async () => {
    if (!createdKey?.value) return;

    try {
      await navigator.clipboard.writeText(createdKey.value);
    } catch {
      setError('Could not copy API key. Please copy it manually.');
    }
  };

  const isBusy = loading || creating || deletingId !== null || savingEdit || catalogLoading;
  const canCreate = !catalogLoading && !catalogError && newName.trim() && selectedPermissions.length > 0;

  const permissionLabel = (code: string) => {
    for (const group of permissionGroups) {
      const match = group.permissions.find(p => p.code === code);
      if (match) {
        return `${group.label}: ${match.actionLabel}`;
      }
    }
    return code;
  };

  const renderPermissionCheckboxes = () => (
    <div className="space-y-4">
      {permissionGroups.map(group => (
        <div key={group.object} className="space-y-2">
          <p className="text-sm font-semibold text-foreground">{group.label}</p>
          <div className="space-y-2 pl-1">
            {group.permissions.map(permission => (
              <label key={permission.code} className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={selectedPermissions.includes(permission.code)}
                  onChange={e => togglePermission(permission.code, e.target.checked)}
                  disabled={isBusy}
                />
                <span>
                  <span className="font-medium">{permission.actionLabel}</span>
                  <span className="text-muted-foreground"> — {permission.description}</span>
                </span>
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-primary/20 bg-muted/40 p-4 space-y-2">
        <p className="text-sm text-muted-foreground">
          API keys power Shortcuts and other automation — not full web login. Need a walkthrough for
          permissions, <code className="text-xs">X-API-KEY</code>, and{' '}
          <code className="text-xs">POST /api/v1/spending</code>?
        </p>
        <Link
          to="/guide/api-keys"
          className={cn(buttonVariants({ variant: 'outline', size: 'sm' }))}
        >
          Open API keys &amp; Shortcuts guide
        </Link>
      </div>

      {createdKey && (
        <div className="space-y-3 rounded-lg border border-amber-300 bg-amber-50 p-4">
          <p className="text-sm font-semibold text-amber-900">Save this API key now</p>
          <p className="text-xs text-amber-800">
            This is shown only once. After you close this message, it cannot be retrieved again.
          </p>
          <Input value={createdKey.value} readOnly className="font-mono text-xs" />
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={handleCopyCreatedKey}>
              Copy
            </Button>
            <Button type="button" onClick={handleDismissCreatedKey}>
              I saved it
            </Button>
          </div>
        </div>
      )}

      {catalogError && (
        <div className="text-red-500 text-sm">{catalogError}</div>
      )}

      {catalogLoading ? (
        <p className="text-sm text-muted-foreground">Loading permissions...</p>
      ) : !catalogError && editingId === null && (
        <div className="space-y-4 rounded-lg border p-4">
          <p className="text-sm font-semibold">Create API key</p>
          <Input
            placeholder="API Key Name"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            disabled={isBusy}
          />
          {renderPermissionCheckboxes()}
          <Button onClick={handleCreate} loading={creating} disabled={isBusy || !canCreate}>
            Create
          </Button>
        </div>
      )}

      {!catalogLoading && !catalogError && editingId !== null && (
        <div className="space-y-4 rounded-lg border border-primary/30 p-4">
          <p className="text-sm font-semibold">Edit permissions</p>
          {renderPermissionCheckboxes()}
          <div className="flex gap-2">
            <Button onClick={() => handleSaveEdit(editingId)} loading={savingEdit} disabled={isBusy}>
              Save
            </Button>
            <Button variant="outline" onClick={cancelEdit} disabled={isBusy}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {error && <div className="text-red-500 text-sm">{error}</div>}

      <ul className="space-y-2">
        {apiKeys.map(key => (
          <li key={key.id} className="flex flex-col gap-2 border rounded px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <p className="truncate font-medium">{key.name}</p>
              <div className="mt-1 flex flex-wrap gap-1">
                {(key.permissions ?? []).map(code => (
                  <span
                    key={code}
                    className="inline-flex rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground"
                  >
                    {permissionLabel(code)}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => startEdit(key)}
                disabled={isBusy || editingId !== null}
              >
                Edit
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => handleDelete(key.id)}
                loading={deletingId === key.id}
                disabled={isBusy}
              >
                Delete
              </Button>
            </div>
          </li>
        ))}
        {apiKeys.length === 0 && !loading && <li className="text-muted-foreground text-sm">No API keys</li>}
      </ul>
    </div>
  );
}
